import { DateRange } from "@/components/controls/DateRangePicker";
import {
  ISOStringFormat,
  differenceInDays,
  format,
  formatDistanceToNow,
  formatISO,
  getHours,
  isToday,
  isValid,
  isYesterday,
  parse,
  parseISO
} from "date-fns";
import { getCurrentLocale } from "./locale";
import { TFunction } from "i18next";
import { ensureDate } from "./dateRange";
import { isNull } from "lodash";

/**
 * Parses an ISO date string (e.g. "2026-02-13T00:00:00.000Z") into a Date
 * object that represents the **calendar date** in local time, regardless of
 * the user's timezone.
 *
 * The classic bug: `new Date("2026-02-13T00:00:00.000Z")` in New York (UTC-5)
 * gives Feb 12 at 7 PM local — the wrong calendar day. This helper extracts
 * the YYYY-MM-DD portion and constructs a local-midnight Date so the calendar
 * day is always correct.
 *
 * Safe to call with Date objects (returned as-is) or non-ISO strings (falls
 * back to `new Date()`).
 */
export const toLocalDate = (value: string | Date | null | undefined): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;

  // Match ISO-like strings that start with YYYY-MM-DD
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, yearStr, monthStr, dayStr] = match;
    // Construct a local date (month is 0-indexed)
    return new Date(
      parseInt(yearStr, 10),
      parseInt(monthStr, 10) - 1,
      parseInt(dayStr, 10)
    );
  }

  // Fallback for non-ISO strings
  return new Date(value);
};

// Date-only values are stored as UTC midnight; anything else is a real moment in time.
export const isUTCMidnight = (value: string): boolean => {
  const date = new Date(value);
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0
  );
};

// Calendar day of a timestamp: the stored day for date-only values, the local day for timed ones.
export const toCalendarDate = (value: string): Date =>
  isUTCMidnight(value) ? toLocalDate(value) : new Date(value);

// Edit-form date and time for a stored activity moment; null when it can't be read
export const toActivityFormValues = (
  value: string
): { date: string; time: string } | null => {
  const day = toCalendarDate(value);
  if (!isValid(day)) return null;
  return {
    date: format(day, "yyyy-MM-dd"),
    time: isUTCMidnight(value) ? "" : format(new Date(value), "h:mm a")
  };
};

/**
 * Converts a Date object to a date-only ISO string: "YYYY-MM-DD".
 * This avoids the UTC midnight timezone trap by not appending a time or "Z".
 * When parsed back with `toLocalDate()`, the calendar day is preserved.
 */
export const toDateOnlyISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getCurrentTimeUTC = () =>
  Math.floor(new Date().getTime() / 1000.0);

export const getRelativeTime = (date: Date) => {
  return formatDistanceToNow(date, { addSuffix: true });
};

export const simpleTime = (
  utcDate: Date,
  locale: string = getCurrentLocale()
): string => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    timeZone
  };
  return new Intl.DateTimeFormat(locale, options).format(utcDate);
};

export const simpleDate = (
  date: Date,
  monthFormat: "long" | "short" = "short",
  hideCurrentYear: boolean = false,
  dayBeforeMonth: boolean = false,
  hideDay: boolean = false
): string => {
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();
  const shouldShowYear = !(hideCurrentYear && isCurrentYear);

  const day = date.getDate();

  const monthFormatter = new Intl.DateTimeFormat(getCurrentLocale(), {
    month: monthFormat
  });
  const month = monthFormatter.format(date);

  const year = shouldShowYear ? date.getFullYear() : null;

  if (hideDay) {
    return year ? `${month} ${year}` : month;
  }

  if (dayBeforeMonth) {
    return year ? `${day} ${month} ${year}` : `${day} ${month}`;
  } else {
    // Default format (month before day)
    return year ? `${month} ${day}, ${year}` : `${month} ${day}`;
  }
};

// Returns null rather than throwing on a malformed timestamp: simpleDate's
// Intl formatter raises RangeError on an Invalid Date.
export const formatIsoDate = (
  isoTimestamp: string | undefined
): string | null => {
  if (!isoTimestamp) return null;
  const parsedDate = parseISO(isoTimestamp);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return simpleDate(parsedDate, "short");
};

export const formatGeneratedAtDate = (iso: string | undefined): string | null =>
  formatIsoDate(iso);

export const formatToMonthDay = (isoString: ISOStringFormat) => {
  const date = toCalendarDate(isoString);
  const month = date.toLocaleString(getCurrentLocale(), { month: "short" });
  const day = date.getDate().toString().padStart(2, "0");
  return `${month} ${day}`;
};

export const formatMonthYear = (
  dateString: string,
  shortMonth?: boolean
): string => {
  const [month, year] = dateString.split("/");
  const fullYear = year.length === 2 ? parseInt(`20${year}`) : parseInt(year);
  const date = new Date(fullYear, parseInt(month) - 1, 1);
  const formatter = new Intl.DateTimeFormat(getCurrentLocale(), {
    year: "numeric",
    month: shortMonth ? "short" : "long"
  });
  return formatter.format(date);
};

export const formatMonthYearDateTime = (
  date: ISOStringFormat,
  shortMonth: boolean = false,
  locale: string = getCurrentLocale()
): string => {
  try {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      console.warn(`Invalid date provided to formatMonthYearDateTime: ${date}`);
      return "Invalid Date";
    }

    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: shortMonth ? "short" : "long"
    }).format(parsedDate);
  } catch (error) {
    console.error(
      `Error formatting date in formatMonthYearDateTime: ${date}`,
      error
    );
    return "Invalid Date";
  }
};

export const formatFullDate = (
  dateString: string | Date,
  includeYear: boolean = true,
  includeDayOfWeek: boolean = false
): string => {
  const date = toLocalDate(dateString);

  const formatter = new Intl.DateTimeFormat(getCurrentLocale(), {
    year: includeYear ? "numeric" : undefined,
    month: "short",
    day: "numeric",
    weekday: includeDayOfWeek ? "long" : undefined
  });

  return formatter.format(date);
};

export const areRangesEqual = (a?: DateRange, b?: DateRange): boolean => {
  if (!a || !b) return a === b;

  const aFromDate = ensureDate(a.fromDate);
  const bFromDate = ensureDate(b.fromDate);
  const aToDate = ensureDate(a.toDate);
  const bToDate = ensureDate(b.toDate);

  if (!aFromDate || !bFromDate) return false;

  return (
    aFromDate.getTime() === bFromDate.getTime() &&
    (!aToDate || !bToDate || aToDate.getTime() === bToDate.getTime())
  );
};

export const lowerCaseTime = (time: string) =>
  time.toLowerCase().replace(" ", "");

export const formatTimeRange = (
  startTime: string,
  endTime: string,
  isLowerCase = false
) => {
  if (!startTime || !endTime) return "";
  // Get "AM" or "PM"
  const startSuffix = startTime.slice(-2);
  const endSuffix = endTime.slice(-2);

  let formattedEndTime = endTime;

  let formattedStartTime =
    startSuffix === endSuffix ? startTime.slice(0, -3) : startTime;

  if (isLowerCase) {
    formattedStartTime = lowerCaseTime(formattedStartTime);
    formattedEndTime = lowerCaseTime(formattedEndTime);
  }

  return `${formattedStartTime}-${formattedEndTime}`;
};

export const parseTimeString = (
  timeString: string
): { hour: number; minute: number } | null => {
  try {
    const [time, period] = timeString.split(" ");
    const [hourStr, minuteStr] = time.split(":");
    let hour = parseInt(hourStr);
    const minute = parseInt(minuteStr);

    // Convert to 24-hour format
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    if (hour >= 0 && hour < 24 && minute >= 0 && minute < 60) {
      return { hour, minute };
    }
    return null;
  } catch {
    return null;
  }
};

export const getUTCDateTime = (date: string, time?: string): string => {
  if (!time) {
    return date;
  }
  const parsed = parseTimeString(time);
  if (!parsed) {
    return date;
  }
  const datePart = date.split("T")[0];
  const time24 = `${parsed.hour.toString().padStart(2, "0")}:${parsed.minute.toString().padStart(2, "0")}`;
  return new Date(`${datePart}T${time24}`).toISOString();
};

export const getEnhancedRelativeTime = (
  t: TFunction<"translation", undefined>,
  date: Date | ISOStringFormat | null,
  monthFormat: "long" | "short" = "short",
  maxDays: number = 6
): string => {
  try {
    if (date === null) {
      return "";
    }

    const dateObj = typeof date === "string" ? toLocalDate(date) : date;

    if (isToday(dateObj)) {
      return t("dateTime.today");
    }

    if (isYesterday(dateObj)) {
      return t("dateTime.yesterday");
    }

    const now = new Date();
    const daysDifference = differenceInDays(now, dateObj);

    // For dates 2-maxDays days ago
    if (daysDifference <= maxDays && daysDifference >= 2) {
      return formatDistanceToNow(dateObj, { addSuffix: true });
    }

    // For older dates
    return `${t("dateTime.on")} ${simpleDate(dateObj, monthFormat)}`;
  } catch (error) {
    return "Invalid date";
  }
};

export const calculateMonthsBetweenDates = (
  startDate: ISOStringFormat | null | undefined,
  endDate: ISOStringFormat | null | undefined
): number | null => {
  if (!startDate || !endDate) {
    return null;
  }

  const start = toLocalDate(startDate as string);
  const end = toLocalDate(endDate as string);
  const yearsDiff = end.getFullYear() - start.getFullYear();
  const monthsDiff = end.getMonth() - start.getMonth();
  let totalMonths = yearsDiff * 12 + monthsDiff;
  if (end.getDate() < start.getDate()) {
    // If the end date is earlier in the month than the start date,
    // we haven't completed a full month
    totalMonths -= 1;
  }

  return totalMonths === 0 ? 1 : totalMonths;
};

export const isOverdue = (date: Date | ISOStringFormat | null): boolean => {
  if (isNull(date)) return false;
  const dateToCheck = typeof date === "string" ? toLocalDate(date) : date;
  const today = new Date();
  return dateToCheck.getTime() < today.getTime() && !isToday(dateToCheck);
};

// Check if a date is within X days of another date
export const isWithinDays = (date: Date, baseDate: Date, days: number) => {
  const diffTime = Math.abs(date.getTime() - baseDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= days;
};

// A date and time to the minute, e.g. "Oct 5, 2026, 10:00 AM".
export const formatDateTimeToMinute = (isoString: string): string =>
  new Intl.DateTimeFormat(getCurrentLocale(), {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(isoString));

export const formatISOToDateTimeString = (
  isoString: ISOStringFormat,
  locale: string = getCurrentLocale()
): string => {
  const date = new Date(isoString);

  const monthFormatter = new Intl.DateTimeFormat(locale, { month: "short" });
  const dayFormatter = new Intl.DateTimeFormat(locale, { day: "2-digit" });
  const yearFormatter = new Intl.DateTimeFormat(locale, { year: "numeric" });

  const month = monthFormatter.format(date);
  const day = dayFormatter.format(date);
  const year = yearFormatter.format(date);

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit"
  });

  const formattedTime = timeFormatter.format(date);

  return `${month} ${day} ${year} at ${formattedTime}`;
};

export const getTimeOfDay = (
  date: Date
): "morning" | "afternoon" | "evening" | "night" => {
  const hour = getHours(date);

  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 22) return "evening";
  return "night";
};

export const formatLastDayOfMonth = (month: number, year: number): string => {
  const lastDay = new Date(year, month, 0);
  return format(lastDay, "d MMM yyyy");
};

export const localTimeStringToIso = (timeString: string): string => {
  const date = parse(timeString, "h:mm a", new Date());
  if (Number.isNaN(date.getTime())) return timeString;
  return formatISO(date);
};

export const isoToTimeString = (iso: string): string => {
  const date = parseISO(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return format(date, "h:mm a");
};

/** "8PM" for whole hours, "8:30PM" otherwise (no space, uppercase AM/PM). */
export const isoToShortTime = (iso: string): string => {
  const date = parseISO(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const hours = format(date, "h");
  const minutes = format(date, "mm");
  const ampm = format(date, "a");
  return minutes === "00" ? `${hours}${ampm}` : `${hours}:${minutes}${ampm}`;
};

/** "dd MMM yyyy" — e.g. "25 May 2026". Falls back to em dash for null/invalid. */
export const isoToShortDate = (iso: string | undefined | null): string => {
  if (!iso) return "—";
  const parsed = parseISO(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return format(parsed, "dd MMM yyyy");
};

/**
 * English ordinal suffix for an integer: 1 → "1st", 2 → "2nd", 11 → "11th", etc.
 * Handles the 11/12/13 exception (which take "th" despite ending in 1/2/3).
 */
export const ordinal = (n: number): string => {
  if (n >= 11 && n <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

export const formatMonthDayOrdinal = (date: Date): string => {
  const month = date.toLocaleString("en-US", {
    month: "long",
    timeZone: "UTC"
  });
  return `${month} ${ordinal(date.getUTCDate())}`;
};
